import { Typography } from 'antd';
import { DateTime } from 'luxon';

import {
    AuditEntityType,
    I9Status,
    LicenseType,
    ListProfileDto,
    NurseProfileCompletenessDto,
    NurseStatePracticeAuthorizationCode,
    PayrollStatus,
    Roles,
    State,
} from '@clh/api-client';
import { HumlaDateTime } from '@clh/util';

import { ActionButton } from '../../action-button';
import AuditHistoryButton from '../../audit-history/button';
import { DisplayValue } from '../../display-value';
import { useApiClient } from '../../hooks/use-api-client';
import { useSelection } from '../../hooks/use-selection';
import RecordDetails from '../../record-details/record-details';
import MatchQualificationCheck from '../match-qualification-check-form';

const Text = Typography.Text;

const { ENVIRONMENT_NAME } = ENV_CONFIG_VARS;

const payrollStatus = (status: PayrollStatus) => (
    <div>
        {
            {
                [PayrollStatus.OnboardingCompleted]: (
                    <>
                        <b>Completed</b> <br />
                        Nurse is fully onboarded and will receive payment
                    </>
                ),
                [PayrollStatus.OnboardingStarted]: (
                    <>
                        <b>Incomplete</b> <br />
                        Nurse has started but not completed payroll onboarding
                    </>
                ),
                [PayrollStatus.Eligible]: (
                    <>
                        <b>Eligible</b> <br />
                        Nurse is eligible, but has not started payroll
                        onboarding
                    </>
                ),
                [PayrollStatus.Ineligible]: (
                    <>
                        <b>Not currently eligible</b> <br />
                        Nurse does not have a verified license in their profile
                    </>
                ),
                [PayrollStatus.InvalidAddress]: (
                    <>
                        <b>Invalid address</b> <br />
                        The address entered for this nurse's profile could not
                        be validated as a real address
                    </>
                ),
                [PayrollStatus.ContractNotSigned]: (
                    <>
                        <b>Contract not signed</b> <br />
                        Nurse cannot view payroll section until they sign their
                        offer letter
                    </>
                ),
            }[status]
        }
    </div>
);

export default function ProfileTab() {
    const api = useApiClient();
    const { selection } = useSelection<ListProfileDto>();

    const nurseProfileCompleteness = (
        completeness: NurseProfileCompletenessDto
    ) => {
        if (!completeness) {
            return <>N/A</>;
        }

        const canFastTrack =
            !Object.values(completeness).every(Boolean) &&
            api &&
            ENVIRONMENT_NAME !== 'production';

        return (
            <div>
                <DisplayValue value={completeness} />
                {canFastTrack && (
                    <ActionButton
                        successMessage="Profile fast tracked"
                        action={async () => {
                            await api.adminControllerFastTrackProfile({
                                profileId: selection.id,
                            });
                            await api.nurseStatePracticeAuthorizationControllerUpdateNurseStatePracticeAuthorizations(
                                {
                                    nurseProfileId: selection.id,
                                    updateNurseStatePracticeAuthorizationsDto: {
                                        authorizedStates: Object.values(
                                            State
                                        ).map((s) => ({
                                            practiceCode:
                                                NurseStatePracticeAuthorizationCode.Authorized,
                                            state: s,
                                        })),
                                        nursysTransactionId: undefined,
                                        licenseType: LicenseType.Rn,
                                    },
                                }
                            );
                        }}
                    >
                        Fast Track
                    </ActionButton>
                )}
            </div>
        );
    };

    if (!api) {
        return null;
    }

    return (
        <>
            <RecordDetails
                fields={{
                    firstName: true,
                    lastName: true,
                    id: (id) => (
                        <Text code={true} copyable={true}>
                            {id}
                        </Text>
                    ),
                    email: (email) => <Text copyable={true}>{email}</Text>,
                    phoneNumber: (val) => <>{val?.number || ''}</>,
                    ncsbnId: (ncsbnId) =>
                        ncsbnId ? <Text copyable={true}>{ncsbnId}</Text> : null,
                    roles: true,
                    permissions: true,
                    nurseProfileCompleteness,
                    payrollStatus,
                    updatedAt: (updatedAt: Date) => (
                        <div style={{ textAlign: 'right' }}>
                            <AuditHistoryButton
                                entityId={selection.id}
                                entityType={AuditEntityType.Profile}
                            >
                                {DateTime.fromJSDate(updatedAt).toLocaleString(
                                    HumlaDateTime.DATETIME_SHORT
                                )}
                            </AuditHistoryButton>
                        </div>
                    ),
                    isBlocked: (isBlocked) =>
                        isBlocked ? (
                            <ActionButton
                                action={async () => {
                                    await api?.accountControllerUnblockAccount({
                                        profileId: selection.id,
                                    });
                                }}
                                successMessage="User Reenabled"
                            >
                                Reenable Profile
                            </ActionButton>
                        ) : (
                            <ActionButton
                                action={async () => {
                                    await api?.accountControllerBlockAccount({
                                        profileId: selection.id,
                                    });
                                }}
                                successMessage="User Disabled"
                            >
                                Disable Profile
                            </ActionButton>
                        ),
                }}
                getRecord={async () => {
                    const [
                        profile,
                        nurseProfileCompleteness,
                        { roles },
                        { permissions },
                        { avatar },
                    ] = await Promise.all([
                        api.profileControllerGetProfile({
                            profileId: selection.id,
                        }),
                        selection.roles.includes(Roles.Nurse)
                            ? api.nurseControllerGetProfileCompleteness({
                                  profileId: selection.id,
                              })
                            : null,
                        api.profileControllerGetUserRoles({
                            profileId: selection.id,
                        }),
                        api.profileControllerGetUserPermissions({
                            profileId: selection.id,
                        }),
                        api
                            .profileControllerGetAvatar({
                                profileId: selection.id,
                            })
                            .catch(() => ({
                                avatar: null,
                            })),
                    ]);

                    return {
                        ...profile,
                        avatar,
                        roles,
                        permissions,
                        nurseProfileCompleteness,
                    };
                }}
                cacheKey={
                    'profileControllerGetFullyLoadedProfile' + selection.id
                }
            />
            <Typography.Title level={5}>Payroll</Typography.Title>
            <RecordDetails
                getRecord={async () =>
                    api.payrollControllerGetProfile({ profileId: selection.id })
                }
                fields={{
                    createdAt: true,
                    onboarded: true,
                    zealEmployeeId: true,
                    i9Status: (i9Status) => {
                        if (i9Status === I9Status.None) {
                            return (
                                <div>
                                    None{' '}
                                    <ActionButton
                                        size="small"
                                        successMessage="I9 link copied to clipboard"
                                        action={async () => {
                                            const result =
                                                await api.payrollControllerGetI9Link(
                                                    {
                                                        profileId: selection.id,
                                                    }
                                                );
                                            await navigator.clipboard.writeText(
                                                result.link
                                            );
                                        }}
                                    >
                                        Generate I9 Link
                                    </ActionButton>
                                </div>
                            );
                        } else if (i9Status === I9Status.Uploaded) {
                            return <>I9 Form Complete</>;
                        } else {
                            return <>N/A</>;
                        }
                    },
                    isSalaried: true,
                }}
                cacheKey={'profileControllerGetPayrollProfile' + selection.id}
            />
            <MatchQualificationCheck profileId={selection.id} />
        </>
    );
}
